import { baseRoute } from '@/globals';

/**
 * Team-aware URLs: `<app base>/t/<team-slug>/<route>`.
 *
 * The team slug lives in the vue-router history base rather than in each
 * route, so every existing `router.push('/campaigns')`, `<router-link>` and
 * `router.resolve().href` carries the tab's team without knowing about it.
 * `router.options.base` stays the plain app base.
 */
export const TEAM_SEGMENT = 't';
export const NO_TEAM_SLUG = '-';

const cleanPath = (path) => path.replace(/\/(?:\s*\/)+/g, '/');

// Mirrors vue-router's normalizeBase: leading slash, no trailing slash.
export const normalizeBase = (base = '') => {
  let normalized = (base || '/').replace(/^https?:\/\/[^/]+/, '');
  if (normalized.charAt(0) !== '/') normalized = '/' + normalized;
  return normalized.replace(/\/+$/, '');
};

/**
 * Team `name` is stored as `Str::slug(label, '_')` (letters, digits and
 * underscores), so swapping underscores for hyphens is lossless.
 */
export const toTeamSlug = (value = '') => {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
};

export const teamSlug = (team) => {
  if (!team) return '';
  return toTeamSlug(team.name) || team.id || '';
};

export const findTeamBySlug = (teams = [], slug = '') => {
  if (!slug) return null;
  const wanted = toTeamSlug(slug);
  return (
    teams.find((team) => teamSlug(team) === wanted) ||
    teams.find((team) => team.id === slug) ||
    null
  );
};

export const teamBase = (appBase, slug) => {
  const base = normalizeBase(appBase);
  if (!slug) return base;
  return cleanPath(`${base}/${TEAM_SEGMENT}/${encodeURIComponent(slug)}`);
};

/**
 * Split a browser pathname into the team slug (if any) and the router base
 * that includes it.
 */
export const parseTeamPath = (pathname = '', appBase = '/') => {
  const base = normalizeBase(appBase);
  const lowerPath = pathname.toLowerCase();
  const prefix = `${base}/${TEAM_SEGMENT}/`.toLowerCase();

  if (!lowerPath.startsWith(prefix)) {
    return { slug: '', base };
  }

  const rawSlug = pathname.slice(prefix.length).split('/')[0];
  if (!rawSlug) {
    return { slug: '', base };
  }

  let slug = rawSlug;
  try {
    slug = decodeURIComponent(rawSlug);
  } catch (e) {
    // keep the raw segment when it is not valid URI encoding
  }

  return { slug, base: pathname.slice(0, prefix.length + rawSlug.length) };
};

let routeTeamSlug = '';

export const appBase = () => normalizeBase(baseRoute());

/**
 * Called once while building the router: reads the team slug from the URL the
 * tab was opened with and returns the history base to use.
 */
export const initialRouterBase = (pathname = window.location.pathname) => {
  const parsed = parseTeamPath(pathname, appBase());
  routeTeamSlug = parsed.slug;
  return parsed.base;
};

export const getRouteTeamSlug = () => routeTeamSlug;

export const currentRouterBase = (router) => {
  return normalizeBase(router?.history?.base ?? appBase());
};

/**
 * Point the router at a team (or no team) and rewrite the current URL in
 * place, without a reload or a new history entry.
 */
export const setRouterTeam = (router, slug = '') => {
  routeTeamSlug = slug || '';
  if (!router?.history) return;

  const base = teamBase(appBase(), routeTeamSlug);
  router.history.base = base;

  // ensureURL() can't be used: the old URL may still resolve to the same
  // route under the new base, so it would skip the rewrite.
  const current = router.history.current;
  if (!current?.matched?.length) return;

  const url = cleanPath(`${base}${current.fullPath}`);
  const { pathname, search, hash } = window.location;
  if (url !== pathname + search + hash) {
    window.history.replaceState(window.history.state, '', url);
  }
};

/**
 * Absolute URL for a full-page load of `path` on the given team.
 */
export const teamUrl = (slug, path = '/') => {
  const base = teamBase(appBase(), slug);
  const route = path.startsWith('/') ? path : `/${path}`;
  return window.location.origin + cleanPath(`${base}${route}`);
};
